import { NavLink } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { NAV_GROUPS, type NavItem } from '@/routes/paths';
import { useUIStore } from '@/shared/stores/ui.store';
import { useRole } from '@/shared/contexts/RoleContext';
import { ZynxMark, ZynxWordmark } from '@/shared/components/brand/ZynxLogo';

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const { can, hasRole } = useRole();

  const visible = (item: NavItem) =>
    (!item.permission || can(item.permission)) && (!item.roles || hasRole(...item.roles));

  // Filter items per group, then drop any group left empty.
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(visible) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-on-surface/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-outline bg-surface transition-all duration-200 ease-standard lg:static lg:translate-x-0',
          collapsed ? 'w-[72px]' : 'w-64',
          sidebarOpen ? 'translate-x-0 shadow-surface-2' : '-translate-x-full',
        )}
      >
        {/* Brand */}
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <ZynxMark className="h-8 w-8 shrink-0 text-lg" />
            {!collapsed && <ZynxWordmark tone="dark" className="truncate text-data-lg" />}
          </div>
          <button
            className="rounded-md p-1 text-on-surface-variant hover:bg-surface-variant lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Grouped navigation */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4 pt-1">
          {groups.map((group, gi) => (
            <div key={group.label ?? `group-${gi}`} className="space-y-1">
              {group.label && !collapsed && (
                <p className="px-3 pb-1 text-label-caps uppercase tracking-wider text-on-surface-variant/70">
                  {group.label}
                </p>
              )}
              {group.label && collapsed && gi > 0 && (
                <div className="mx-auto mb-1 h-px w-6 bg-outline" aria-hidden />
              )}
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-DEFAULT px-3 py-2 text-data-md font-medium outline-none transition-colors duration-150 ease-standard',
                      'focus-visible:ring-2 focus-visible:ring-primary/40',
                      isActive
                        ? 'bg-primary-container text-on-primary-container'
                        : 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface',
                      collapsed && 'justify-center px-0',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Active accent bar */}
                      <span
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-opacity',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                        aria-hidden
                      />
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {!collapsed && (
          <div className="border-t border-outline px-4 py-3 text-label-caps uppercase tracking-wider text-on-surface-variant/70">
            ZYNX · by DurraniTech
          </div>
        )}
      </aside>
    </>
  );
}
